import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import Button from '../Button/Button';
import Checkbox from '../Checkbox/Checkbox';
import FormGroup from '../FormGroup/FormGroup';
import FormItem from '../FormItem/FormItem';
import Input from '../Input/Input';

import useApi from '../../utils/api';
import classNames from '../../utils/classNames';
import { paymentPeriods, paymentTypes } from '../../utils/constants';
import formatAmount, { getPaymentPeriod } from '../../utils/formatAmount';
import { validateEmail, validateName, validatePhone, validateReqiured } from '../../utils/validate';

import styles from './payment-form.scss';

export default function PaymentForm(props) {
    const api = useApi();
    const { t } = useTranslation();

    const { data, previewMode } = props;
    const {
        title,
        is_recurrent: isRecurrent,
        default_regular_amount: regularAmount,
        default_first_amount: firstAmount,
        creditable: isCreditable,
        external_fields: additionalFields,
        payment_object: paymentObject,
        once_per_days = 30,
        link,
        company_name,
        company_offer_link = 'https://www.tinkoff.ru/kassa/solution/autopayments/',
    } = data;

    const [fields, setFields] = React.useState([
        { name: 'common-name', type: 'text', value: '', label: t('paymentForm.name'), required: true, error: null },
        { name: 'common-email', type: 'email', value: '', label: t('paymentForm.email'), required: true, error: null },
        {
            name: 'common-phone_number',
            type: 'tel',
            value: '',
            label: t('paymentForm.phone'),
            placeholder: 'Например, 89101234567',
            required: true,
            error: null,
        },
    ]);
    const [hasAgreement, setHasAgreement] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [yooKassa, setYooKassa] = React.useState(false);
    const [yooKassaDone, setYooKassaDone] = React.useState(null);

    function handleInputChange(e) {
        const input = e.target;
        const newFields = fields.map((field) => {
            if (field.name === input.name) {
                if (field.name === 'common-phone') {
                    field.value = input.value.replace(/\s/gi, '');
                } else {
                    field.value = input.value;
                }
                field.error = null;
            }

            return field;
        });
        setFields(newFields);
    }

    function handleCheck(isChecked) {
        setHasAgreement(isChecked);
    }

    /**
     * Validate specific form field.
     * @param {object} field Form field object.
     * @returns null if no errors, or Error object instead.
     */
    function validateFormField(field) {
        if (field.required && validateReqiured(field.value) !== null) {
            return validateReqiured(field.value);
        }

        if (field.name === 'common-name') {
            return validateName(field.value);
        }

        if (field.type === 'tel') {
            return validatePhone(field.value);
        }

        if (field.type === 'email') {
            return validateEmail(field.value);
        }

        return null;
    }

    /**
     * Validate all form fields.
     * @returns {Boolean} Is form valid
     */
    function isFormValid() {
        let errors = 0;
        const newFields = fields.map((field) => {
            field.error = validateFormField(field);

            if (field.error !== null) errors++;

            return field;
        });
        setFields(newFields);

        if (previewMode && errors === 0) {
            alert('Форма заполнена верно. Это предосмотр, дальше ничего не произойдет.');

            return false;
        }

        return errors === 0;
    }

    function prepareFormData() {
        const preparedData = {
            external_fields_answers: {},
        };

        fields.forEach((field) => {
            if (field.name.indexOf('common-') === 0) {
                const fieldName = field.name.replace('common-', '');
                preparedData[fieldName] = field.value;
            } else {
                const fieldName = field.name.replace('additional-field-', '');
                preparedData.external_fields_answers[fieldName] = {
                    field_name: field.label,
                    field_value: field.value,
                };
            }

            return field;
        });

        return preparedData;
    }

    /**
     * Handles form submit.
     * Validates all fields and transfer data to the server.
     */
    function submit() {
        const isValid = isFormValid();

        const formData = prepareFormData();

        if (isValid) {
            setLoading(true);
            api.performPayment(link, formData)
                .then(({ payment_url, payment_token }) => {
                    if (payment_url) {
                        window.location.replace(payment_url);
                    } else if (payment_token) {
                        // Instantiate YooKassa widget.
                        setYooKassa(true);

                        const checkout = new window.YooMoneyCheckoutWidget({
                            confirmation_token: payment_token,
                            error_callback: (error) => {
                                console.error(error);
                            },
                        });

                        checkout.on('success', () => {
                            setYooKassaDone({
                                title: 'Спасибо',
                                description:
                                    'Платёж успешно завершен. Чек об оплате будет отправлен на вашу электронную почту.',
                            });
                        });

                        checkout.on('fail', () => {
                            setYooKassaDone({
                                title: 'Оплата не завершена',
                                description: 'Пожалуйста, попробуйте обновить страницу и совершить платёж ещё раз.',
                            });
                        });

                        checkout.render('yooKassaForm');
                    } else {
                        throw new Error('Cannot resolve payment page link.');
                    }
                })
                .catch((error) => {
                    console.error(error);
                    alert('Ошибка при получении ссылки на страницу оплаты');
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    /**
     * Handles submit form by credit button.
     */
    function submitCredit() {
        const isValid = isFormValid();
        const formData = prepareFormData();

        if (!isValid) {
            return false;
        }

        setLoading(true);
        api.performCreditPayment(link, formData)
            .then(({ credit_url }) => {
                if (credit_url) {
                    window.location.assign(credit_url);
                } else {
                    throw new Error('Cannot find credit_url in server response.');
                }
            })
            .catch((error) => {
                console.error(error);
                alert('Не удалось применить покупку через кредитования. Возможно, сервисы кредитовария недоступны.');
            })
            .finally(() => {
                setLoading(false);
            });
    }

    /**
     * Render form fields.
     * @returns Array of FormItem react-nodes.
     */
    function renderFields() {
        return fields.map((field) => {
            return (
                <FormItem key={field.name} label={field.label} required={field.required} error={field.error}>
                    <Input
                        type={field.type}
                        name={field.name}
                        value={field.value}
                        placeholder={field.placeholder}
                        onChange={handleInputChange}
                    />
                </FormItem>
            );
        });
    }

    /**
     * Get Regular and First payment amounts.
     * @returns {array} Array containing Regular amount and first amount.
     */
    function getPaymentAmounts() {
        const regularPaymentAmount = data.regular_amount ?? regularAmount;
        let firstPaymentAmount = regularPaymentAmount;

        if (isRecurrent && (Number(data.first_amount) || Number(firstAmount))) {
            firstPaymentAmount = data.first_amount || firstAmount;
        }

        return [regularPaymentAmount, firstPaymentAmount];
    }

    /**
     * Returns text under action button if default amount
     * is not equals first amount.
     */
    function getTextUnderButton() {
        const [regular, first] = getPaymentAmounts();

        if (regular !== first && isRecurrent) {
            return (
                <span className={styles.promoTip}>
                    Далее – {regular} ₽ / {getPaymentPeriod(once_per_days)}
                </span>
            );
        }

        return null;
    }

    /**
     * On additionalFields prop update.
     */
    useEffect(() => {
        if (Array.isArray(additionalFields)) {
            const addFields = additionalFields.map((field, i) => {
                return {
                    name: 'additional-field-' + (field.id ?? i),
                    type: 'text',
                    value: '',
                    label: field?.field_name || 'Новое поле #' + i,
                    required: field?.is_necessary || false,
                    error: null,
                };
            });
            setFields((currentFields) => [...currentFields.slice(0, 3), ...addFields]);
        } else {
            setFields((currentFields) => currentFields.slice(0, 3));
        }
    }, [additionalFields]);

    const [regularPayment, firstPayment] = getPaymentAmounts();

    const submitText =
        (isRecurrent ? t('paymentForm.action.subscription') : t('paymentForm.action.payment')) +
        ` ${formatAmount(firstPayment) || 0} ₽`;
    const titleWrapped = title.replace('\\n', '\n');
    const paymentTypeText = isRecurrent ? 'Подписка' : `Оплата ${paymentTypes[paymentObject]}`;
    const paymentPeriodText = '/ ' + paymentPeriods[once_per_days];

    return (
        <div className={styles.wrapper}>
            {!yooKassaDone && (
                <div className={styles.header}>
                    <h3>{paymentTypeText}</h3>
                    <h2
                        className={classNames(styles.amount, {
                            [styles.recurrent]: isRecurrent,
                        })}
                    >
                        {formatAmount(regularPayment)}
                        <span className={styles.paymentPeriod}>₽ {isRecurrent && paymentPeriodText}</span>
                    </h2>
                    <h4>{title.trim().length > 0 ? titleWrapped : 'Оплата'}</h4>
                    {company_name && <h5>Продавец: {company_name}</h5>}
                </div>
            )}

            {!yooKassa && (
                <div className={styles.content}>
                    <FormGroup title={'Платёжная информация'}>
                        {renderFields()}
                        <Checkbox onCheck={handleCheck}>
                            {t('paymentForm.agreement')}
                            <a href={'https://dispurse.app'} target={'_blank'} rel="noreferrer">
                                {t('paymentForm.privacy')}
                            </a>
                            {isRecurrent ? ' и ' : '.'}
                            {isRecurrent && (
                                <a href={company_offer_link} target={'_blank'} rel="noreferrer">
                                    {t('paymentForm.offer')}
                                </a>
                            )}
                        </Checkbox>
                        <FormItem>
                            <div>
                                <Button
                                    text={submitText}
                                    fill={true}
                                    disabled={!hasAgreement}
                                    loading={loading}
                                    onClick={submit}
                                />

                                {getTextUnderButton()}
                            </div>

                            {isCreditable && (
                                <div className={styles.creditOptions}>
                                    <Button
                                        loading={loading}
                                        disabled={!hasAgreement}
                                        level="secondary"
                                        text={t('paymentForm.credit')}
                                        fill={true}
                                        onClick={submitCredit}
                                    />
                                </div>
                            )}
                        </FormItem>
                    </FormGroup>
                </div>
            )}

            {!yooKassaDone && (
                <div className={styles.yookassa}>
                    <div id={'yooKassaForm'} />
                </div>
            )}

            {yooKassaDone && (
                <div className={styles.complete}>
                    <h1>{yooKassaDone.title}</h1>
                    <p className={styles.paymentStatus}>{yooKassaDone.description}</p>
                </div>
            )}
        </div>
    );
}

PaymentForm.propTypes = {
    data: PropTypes.shape({
        id: PropTypes.number,
        company_name: PropTypes.string,
        title: PropTypes.string,
        is_recurrent: PropTypes.bool,
        default_regular_amount: PropTypes.number,
        regular_amount: PropTypes.number,
        default_first_amount: PropTypes.number,
        first_amount: PropTypes.number,
        external_fields: PropTypes.array,
        payment_object: PropTypes.string,
        once_per_days: PropTypes.number,
        link: PropTypes.string,
        company_offer_link: PropTypes.string,
        creditable: PropTypes.bool,
    }),
    previewMode: PropTypes.bool,
};
